import type { Product } from '@gate31/types/product';

interface ProductFieldValue {
    'product_field_id': number;
    value: string | null;
}

export interface ArticleCareResult {
    care: string | null;
    composition: string | null;
}

// ID дополнительных полей товара в InSales
const PRODUCT_FIELDS = {
    CARE: 52961, // Уход
    COMPOSITION: 51350 // Состав
};

const MESSAGES = {
    NOT_FOUND: 'Товар с таким артикулом не найден. Проверьте артикул и попробуйте ещё раз.',
    EMPTY: 'Для этого артикула информация об уходе и составе пока не заполнена.',
    ERROR: 'Произошла ошибка. Попробуйте повторить действия позже.'
};

class ArticleCareError extends Error {
    name = 'ArticleCareError';
}

const ERRORS = {
    INIT: 'Ошибка инициализации формы поиска ухода по артикулу. DOM-узел не найден',
    FIELDS: 'В форме поиска ухода по артикулу не найдены поле ввода или кнопка',
    RESULT: 'Не найден блок результата поиска ухода по артикулу',
    REQUEST: 'Ошибка запроса товара по артикулу'
};

export class ArticleCare {
    $form: HTMLFormElement;
    $input: HTMLInputElement;
    $button: HTMLButtonElement;
    $clear: HTMLButtonElement | null;
    $result: HTMLElement;
    $accordion: HTMLElement | null;
    $message: HTMLElement;
    $fields: Record<keyof ArticleCareResult, HTMLElement | null>;

    constructor({ formSelector, resultSelector }: { formSelector: string; resultSelector: string }) {
        const $form = document.querySelector<HTMLFormElement>(formSelector);

        if (! $form) {
            throw new ArticleCareError(ERRORS.INIT);
        }

        const $input = $form.querySelector<HTMLInputElement>('input[name="article"]');
        const $button = $form.querySelector<HTMLButtonElement>('button[type="submit"]');

        if (! $input || ! $button) {
            throw new ArticleCareError(ERRORS.FIELDS);
        }

        const $result = document.querySelector<HTMLElement>(resultSelector);
        const $message = $result?.querySelector<HTMLElement>('[data-article-care-message]');

        if (! $result || ! $message) {
            throw new ArticleCareError(ERRORS.RESULT);
        }

        this.$form = $form;
        this.$input = $input;
        this.$button = $button;
        this.$clear = $form.querySelector<HTMLButtonElement>('[data-article-care-clear]');
        this.$result = $result;
        // Аккордеон есть только в ПК-версии: раскрываем его, чтобы показать ответ
        this.$accordion = $result.closest<HTMLElement>('[data-article-care-accordion]');
        this.$message = $message;
        this.$fields = {
            care: $result.querySelector<HTMLElement>('[data-article-care-field="care"]'),
            composition: $result.querySelector<HTMLElement>('[data-article-care-field="composition"]')
        };

        this.toggleButtonState();

        this.$input.addEventListener('input', () => {
            this.toggleButtonState();
        });

        this.$clear?.addEventListener('click', () => {
            this.clearInput();
        });

        this.$form.addEventListener('submit', e => {
            this.formHandler(e);
        });
    }

    getArticle() {
        return this.$input.value.trim();
    }

    toggleButtonState() {
        this.$button.disabled = this.getArticle() === '';

        if (this.$clear) {
            this.$clear.hidden = this.$input.value === '';
        }
    }

    clearInput() {
        this.$input.value = '';
        this.toggleButtonState();
        this.$input.focus();
    }

    formHandler(event: Event) {
        event.preventDefault();

        const article = this.getArticle();

        if (! article) {
            return;
        }

        this.$button.disabled = true;

        this.fetchArticleCare(article)
            .then(result => this.renderResult(result))
            .catch(() => this.renderMessage(MESSAGES.ERROR))
            .finally(() => this.toggleButtonState());
    }

    renderResult(result: ArticleCareResult | null) {
        if (! result) {
            return this.renderMessage(MESSAGES.NOT_FOUND);
        }

        if (! result.care && ! result.composition) {
            return this.renderMessage(MESSAGES.EMPTY);
        }

        (Object.keys(this.$fields) as (keyof ArticleCareResult)[]).forEach(key => {
            const $field = this.$fields[key];
            const $value = $field?.querySelector<HTMLElement>('[data-article-care-value]');
            const value = result[key];

            if (! $field || ! $value) {
                return;
            }

            $value.innerHTML = value ?? '';
            $field.hidden = ! value;
        });

        this.$message.hidden = true;
        this.showResult();
    }

    renderMessage(message: string) {
        Object.values(this.$fields).forEach($field => {
            if ($field) {
                $field.hidden = true;
            }
        });

        this.$message.textContent = message;
        this.$message.hidden = false;
        this.showResult();
    }

    showResult() {
        this.$result.hidden = false;
        this.$accordion?.setAttribute('data-accordion-is-active', 'true');
    }

    /**
     * Ищет товар по артикулу через публичный API InSales и возвращает значения дополнительных полей «Уход» и «Состав».
     * Если товар с таким артикулом не найден — возвращает null.
     */
    async fetchArticleCare(article: string): Promise<ArticleCareResult | null> {
        const products: any = await fetch(`https://api.aerogate31.com/v2/product/sku/${encodeURIComponent(article?.toUpperCase() || '')}`)
            .then(response => {
                if (! response.ok) {
                    throw new ArticleCareError(ERRORS.REQUEST);
                }

                return response.json();
            });

        const fieldValues = products.product_field_values ?? [];
        const getFieldValue = (fieldId: number) => fieldValues
            .find((field: any) => field.product_field_id === fieldId)?.value || null;

        return {
            care: getFieldValue(PRODUCT_FIELDS.CARE),
            composition: getFieldValue(PRODUCT_FIELDS.COMPOSITION)
        };
    }
}
